import { create } from 'zustand';
import { setAccessToken, setUnauthenticatedHandler } from '../api/http.ts';
import * as authApi from '../api/auth.ts';
import type { Namespace, User } from '../api/types.ts';

export interface Features {
  /** 공유 링크·공유 폴더 사용 여부. 서버 운영 정책에서 켜고 끈다. */
  sharing: boolean;
}

interface AuthState {
  user: User | null;
  namespaces: Namespace[];
  features: Features;
  status: 'loading' | 'authenticated' | 'anonymous';
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, displayName: string) => Promise<void>;
  logout: () => Promise<void>;
  bootstrap: () => Promise<void>;
  setUsage: (used: number) => void;
  primaryNamespace: () => Namespace | null;
}

export const useAuth = create<AuthState>((set, get) => ({
  user: null,
  namespaces: [],
  features: { sharing: false },
  status: 'loading',

  login: async (email, password) => {
    const result = await authApi.login(email, password);
    setAccessToken(result.access_token);
    set({ user: result.user, namespaces: result.namespaces, status: 'authenticated' });
    // 로그인 응답에는 기능 플래그가 없으므로 이어서 한 번 더 받아 온다
    await get().bootstrap();
  },

  signup: async (email, password, displayName) => {
    const result = await authApi.signup(email, password, displayName);
    setAccessToken(result.access_token);
    set({ user: result.user, namespaces: result.namespaces, status: 'authenticated' });
    await get().bootstrap();
  },

  logout: async () => {
    try { await authApi.logout(); } finally {
      setAccessToken(null);
      set({ user: null, namespaces: [], status: 'anonymous' });
    }
  },

  /** 새로고침 후 복구: 리프레시 쿠키로 액세스 토큰을 되찾는다. */
  bootstrap: async () => {
    try {
      const { user, namespaces, features } = await authApi.me();
      set({ user, namespaces, features: features ?? { sharing: false }, status: 'authenticated' });
    } catch {
      set({ user: null, namespaces: [], status: 'anonymous' });
    }
  },

  setUsage: (used) => {
    const user = get().user;
    if (user) set({ user: { ...user, used_bytes: used } });
  },

  primaryNamespace: () => get().namespaces[0] ?? null,
}));

setUnauthenticatedHandler(() => {
  setAccessToken(null);
  useAuth.setState({ user: null, namespaces: [], status: 'anonymous' });
});
